// InviteBuyerAdmin.jsx
import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Typography,
  Grid,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  Paper,
} from "@mui/material";
import { API_ENDPOINT } from "../../services/api/resources";

function a11yProps(index) {
  return { id: `invite-tab-${index}`, "aria-controls": `invite-tabpanel-${index}` };
}

// Very small CSV parser good for simple Excel-CSV (no embedded commas/quotes in fields).
// Expect headers: email,company_name
function parseCSV(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const emailIdx = header.indexOf("email");
  const companyIdx = header.indexOf("company_name");
  if (emailIdx === -1) throw new Error("CSV must have 'email' column");
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim());
    const email = cols[emailIdx] || "";
    const company_name = companyIdx >= 0 ? (cols[companyIdx] || "") : "";
    if (!email) continue;
    rows.push({ email, company_name, __row: i + 1 });
  }
  return rows;
}

// Tiny concurrency limiter
async function runWithLimit(items, limit, worker) {
  const results = [];
  let i = 0;
  const workers = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
    while (i < items.length) {
      const idx = i++;
      results[idx] = await worker(items[idx], idx);
    }
  });
  await Promise.all(workers);
  return results;
}

export default function InviteBuyerAdmin() {
  const [tab, setTab] = useState(0);

  // single invite
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // bulk invite
  const [file, setFile] = useState(null);
  const [bulkError, setBulkError] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkReport, setBulkReport] = useState(null); // {processed, success, failed: [{row,email,error}]}
  const [bulkRunning, setBulkRunning] = useState(false);

  const emailError = useMemo(() => {
    if (!email) return "";
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    return ok ? "" : "Enter a valid email";
  }, [email]);

  const canSubmit = useMemo(() => !submitting && !!email && !emailError, [submitting, email, emailError]);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      const res = await axios.post(API_ENDPOINT.invite_buyer_admin, {
        email: email.trim(),
        company_name: companyName?.trim() || "",
      });
      setSuccessMsg(res?.data?.message || "Invitation sent successfully.");
      setEmail("");
      setCompanyName("");
    } catch (err) {
      const apiErr =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setErrorMsg(apiErr);
    } finally {
      setSubmitting(false);
    }
  };

  const onChooseFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setBulkError("");
    setBulkSuccess("");
    setBulkReport(null);
    setBulkProgress({ done: 0, total: 0 });
  };

  const downloadTemplate = () => {
    const csv = "email,company_name\nbuyer1@example.com,Acme Corp\nbuyer2@example.com,Globex Pvt Ltd\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "buyer_admin_invite_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setBulkError("Please select a CSV file.");

    setBulkRunning(true);
    setBulkError("");
    setBulkSuccess("");
    setBulkReport(null);
    setBulkProgress({ done: 0, total: 0 });

    const text = await file.text();
    let rows;
    try {
      rows = parseCSV(text);
      if (!rows.length) throw new Error("No valid rows found.");
    } catch (err) {
      setBulkRunning(false);
      return setBulkError(err.message || "Invalid CSV format.");
    }

    const total = rows.length;
    setBulkProgress({ done: 0, total });

    let success = 0;
    const failed = [];

    const CONCURRENCY = 5; // tune as needed

    await runWithLimit(rows, CONCURRENCY, async (row, idx) => {
      try {
        await axios.post(API_ENDPOINT.invite_buyer_admin, {
          email: row.email.trim(),
          company_name: (row.company_name || "").trim(),
        });
        success += 1;
      } catch (err) {
        const apiErr =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          "Request failed";
        failed.push({ row: row.__row || idx + 2, email: row.email, error: apiErr });
      } finally {
        setBulkProgress((p) => ({ done: p.done + 1, total }));
      }
    });

    setBulkReport({ processed: total, success, failed });
    setBulkSuccess("Bulk invitations processed.");
    setBulkRunning(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth:"100%", margin: '0 auto' }}>
      <Card sx={{ 
        width: "100%", 
        border: "1px solid #e2e8f0", 
        borderRadius: 3, 
        background: "#fff",
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      }}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a365d' }}>
                Invite Buyer Admin
              </Typography>
            </Stack>
          }
          sx={{ pb: 0, px: 3, pt: 2 }}
        />

        <Tabs 
          value={tab} 
          onChange={(_, v) => setTab(v)} 
          sx={{ px: 3, pt: 2 }} 
          aria-label="invite tabs"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Single Invite" {...a11yProps(0)} sx={{ fontSize: '14px', fontWeight: 600 }} />
          <Tab label="Bulk Upload (CSV)" {...a11yProps(1)} sx={{ fontSize: '14px', fontWeight: 600 }} />
        </Tabs>
        <Divider />

        {/* Single Invite Tab */}
        {tab === 0 && (
          <CardContent sx={{ pt: 3, px: 3 }}>
            <Stack component="form" onSubmit={handleSingleSubmit} spacing={3}>
              {successMsg && (
                <Alert severity="success" sx={{ fontSize: "15px", fontWeight: "500" }} onClose={() => setSuccessMsg("")}>
                  {successMsg}
                </Alert>
              )}
              {errorMsg && (
                <Alert severity="error" sx={{ fontSize: "15px", fontWeight: "500" }} onClose={() => setErrorMsg("")}>
                  {errorMsg}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email Address"
                    type="email"
                    value={email}
                    required
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                    size="medium"
                    FormHelperTextProps={{
                      sx: { fontSize: "14px", lineHeight: 1.5, mt: 1 },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '14px' },
                      '& .MuiInputBase-input': { fontSize: '14px', padding: '12px 14px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    size="medium"
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '14px' },
                      '& .MuiInputBase-input': { fontSize: '14px', padding: '12px 14px' }
                    }}
                
                    FormHelperTextProps={{
                      sx: { fontSize: "14px", lineHeight: 1.5, mt: 1 },
                    }}
                  />
                </Grid>
              </Grid>

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit}
                  sx={{ 
                    textTransform: "none", 
                    fontWeight: 600, 
                    fontSize: '16px',
                    padding: '10px 24px',
                    borderRadius: 2,
                    minWidth: 160,
                    height: 48
                  }}
                >
                  {submitting ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={20} />
                      <span>Sending Invitation...</span>
                    </Stack>
                  ) : (
                    "Send Invitation"
                  )}
                </Button>
              </Box>

              <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
                <Typography variant="body1" sx={{ color: "text.secondary", fontSize: '14px', lineHeight: 1.6 }}>
                  An email with a secure link to create Buyer Admin credentials will be sent to the address provided.
                  The recipient will be able to set up their account and access the buyer admin dashboard.
                </Typography>
              </Paper>
            </Stack>
          </CardContent>
        )}

        {/* Bulk Upload Tab */}
        {tab === 1 && (
          <CardContent sx={{ pt: 3, px: 3 }}>
            <Stack component="form" onSubmit={handleBulkSubmit} spacing={3}>
              {bulkSuccess && (
                <Alert severity="success" onClose={() => setBulkSuccess("")} sx={{ fontSize: '15px' }}>
                  {bulkSuccess}
                </Alert>
              )}
              {bulkError && (
                <Alert severity="error" onClose={() => setBulkError("")} sx={{ fontSize: '15px' }}>
                  {bulkError}
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8fafc' }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                    <Button 
                      variant="outlined" 
                      onClick={downloadTemplate}
                      sx={{ fontSize: '14px', padding: '8px 16px' }}
                    >
                      Download CSV Template
                    </Button>

                    <input type="file" accept=".csv" onChange={onChooseFile} style={{ display: "none" }} id="bulk-file-input" />
                    <label htmlFor="bulk-file-input">
                      <Button 
                        component="span" 
                        variant="contained" 
                        disabled={bulkRunning}
                        sx={{ fontSize: '14px', padding: '8px 16px' }}
                      >
                        {file ? "Change File" : "Choose CSV File"}
                      </Button>
                    </label>

                    <Typography variant="body1" sx={{ color: "text.secondary", fontSize: '16px', fontWeight: 500 }}>
                      {file ? `Selected: ${file.name}` : "No file selected"}
                    </Typography>
                  </Stack>

                  {file && !bulkRunning && (
                    <Typography variant="body2" sx={{ color: "success.main", fontSize: '14px' }}>
                      ✓ Ready to upload {file.name}
                    </Typography>
                  )}
                </Stack>
              </Paper>

              {bulkRunning && (
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                      Processing invitations...
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        bulkProgress.total
                          ? Math.min(100, Math.round((bulkProgress.done / bulkProgress.total) * 100))
                          : 0
                      }
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '14px' }}>
                        Progress: {bulkProgress.done}/{bulkProgress.total}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '14px' }}>
                        {bulkProgress.total ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0}%
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              )}

              <Box>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={!file || bulkRunning} 
                  sx={{ 
                    textTransform: "none", 
                    fontWeight: 600, 
                    fontSize: '16px',
                    padding: '10px 24px',
                    borderRadius: 2,
                    minWidth: 160,
                    height: 48
                  }}
                >
                  {bulkRunning ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={20} />
                      <span>Processing...</span>
                    </Stack>
                  ) : (
                    "Upload & Send Invitations"
                  )}
                </Button>
              </Box>

              {bulkReport && (
                <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8fafc' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontSize: '18px', fontWeight: 600 }}>
                    Bulk Invitation Summary
                  </Typography>
                  
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e6f4ea', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700, fontSize: '24px' }}>
                          {bulkReport.processed}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                          Total Processed
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e6f4ea', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700, fontSize: '24px' }}>
                          {bulkReport.success}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                          Successful
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: bulkReport.failed?.length ? '#fce8e6' : '#e6f4ea', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: bulkReport.failed?.length ? 'error.main' : 'success.main', fontWeight: 700, fontSize: '24px' }}>
                          {bulkReport.failed?.length || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                          Failed
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {!!bulkReport.failed?.length && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '16px' }}>
                        Failed Invitations:
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {bulkReport.failed.map((f, idx) => (
                          <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: '#fff' }}>
                            <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
                              Row {f.row}: {f.email}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: 'error.main', mt: 0.5 }}>
                              Error: {f.error}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}

              <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#f0f4f8' }}>
                <Typography variant="body1" sx={{ color: "text.secondary", fontSize: '15px', mb: 1, fontWeight: 600 }}>
                  CSV Format Requirements:
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '14px', lineHeight: 1.6 }}>
                  • Required columns: <b>email</b>, <b>company_name</b><br/>
                  • First row must contain headers<br/>
                  • File must be in CSV format (comma-separated values)<br/>
                  • Maximum recommended file size: 5MB
                </Typography>
              </Paper>
            </Stack>
          </CardContent>
        )}
      </Card>
    </Box>
  );
}